<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { useValuesCompare } from '../../composables';
import type { TrackBy } from '../../../shared/types';
interface Props {
  ariaLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  value?: any;
  falseValue?: any;
  modelValue?: any;
  trackBy?: TrackBy;
}
const props = withDefaults(defineProps<Props>(), {
  ariaLabel: undefined,
  disabled: false,
  invalid: false,
  value: true,
  falseValue: false,
  modelValue: null,
  trackBy: undefined,
});
const emit = defineEmits<{
  'update:modelValue': [active: boolean];
}>();
const value = useVModel(props, 'modelValue', emit, {
  deep: true,
  passive: true,
  defaultValue: props.falseValue,
});
const { equal: checked } = useValuesCompare(value, props.value, props.trackBy);
function onClick(): void {
  if (props.disabled) {
    return;
  }
  value.value = checked.value ? props.falseValue : props.value;
}
</script>

<template>
  <button
    :aria-checked="checked"
    :aria-invalid="props.invalid ? true : undefined"
    :aria-label="ariaLabel"
    :disabled="props.disabled"
    role="switch"
    type="button"
    :class="[
      $style.root,
      {
        [$style.checked]: checked,
        [$style.disabled]: disabled,
      },
    ]"
    @click="onClick"
  >
    <span :class="$style.handle" />
  </button>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/shared';

.root {
  @include shared.reset-button();
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  box-sizing: border-box;
  width: 22px;
  height: 12px;
  border-radius: 6px;
  cursor: pointer;
  border: 2px solid colors.$primary;

  &:focus-visible {
    outline: 1px solid colors.$hyperlink;
    outline-offset: 2px;
  }
}

.checked {
  color: var(--ui2-background-accent-primary, #0e1a32);
  background-color: var(--ui2-background-accent-primary, #0e1a32);
  border-color: var(--ui2-background-accent-primary, #0e1a32);
}

.disabled {
  border-color: colors.$surface-40;
  cursor: not-allowed;
}

.disabled.checked {
  border-color: colors.$action-disabled;
  background-color: colors.$action-disabled;
}

.handle {
  box-sizing: border-box;
  width: 12px;
  height: 12px;
  position: absolute;
  border: inherit;
  border-radius: 50%;
  left: -2px;
  top: -2px;
  background-color: #fff;
  transition: transform 0.2s ease-in-out;
}

.checked .handle {
  transform: translateX(10px);
}
</style>
